import { resolveKeysToVarNames } from "./VariablePicker";

function names(values, key, dataGroup, multiple) {
  const raw = values[key];
  return multiple
    ? resolveKeysToVarNames(raw || [], dataGroup)
    : resolveKeysToVarNames(raw ? [raw] : [], dataGroup);
}

/** Generates SPSS-style pseudo-syntax summarizing the current configuration —
 * a readable, copyable record of the run, not something re-executed by the app. */
export function generateSyntax(analysisType, values, dataGroup) {
  const id = analysisType.id;
  const dv = () => names(values, "dv", dataGroup, false)[0];
  const ivs = () => names(values, "ivs", dataGroup, true);
  const vars = () => names(values, "variables", dataGroup, true);
  const groupVar = () => names(values, "groupVar", dataGroup, false)[0];
  const var1 = () => names(values, "var1", dataGroup, false)[0];
  const var2 = () => names(values, "var2", dataGroup, false)[0];
  const ci = () => Math.round((values.confidenceLevel ?? 0.95) * 100);

  switch (id) {
    case "regression":
    case "driverAnalysis":
      return [
        "REGRESSION",
        "  /MISSING LISTWISE",
        "  /STATISTICS COEFF OUTS CI(" + ci() + ") R ANOVA COLLIN",
        `  /DEPENDENT ${dv() || "?"}`,
        `  /METHOD=${(values.method || "enter").toUpperCase()} ${ivs().join(" ") || "?"} .`,
      ].join("\n");
    case "logistic":
      return [
        `LOGISTIC REGRESSION VARIABLES ${dv() || "?"}`,
        `  /METHOD=ENTER ${ivs().join(" ") || "?"}`,
        `  /CRITERIA=CI(${ci()})`,
        "  /PRINT=CI(" + ci() + ") .",
      ].join("\n");
    case "ttest":
      return [
        `T-TEST GROUPS=${groupVar() || "?"}`,
        "  /MISSING=ANALYSIS",
        `  /VARIABLES=${dv() || "?"}`,
        `  /CRITERIA=CI(${(values.confidenceLevel ?? 0.95).toFixed(2)}) .`,
      ].join("\n");
    case "anova":
      return [
        `ONEWAY ${dv() || "?"} BY ${groupVar() || "?"}`,
        "  /STATISTICS DESCRIPTIVES",
        "  /POSTHOC=TUKEY ALPHA(0.05) .",
      ].join("\n");
    case "chisquare":
      return [
        "CROSSTABS",
        `  /TABLES=${var1() || "?"} BY ${var2() || "?"}`,
        "  /STATISTICS=CHISQ CC PHI .",
      ].join("\n");
    case "correlation":
      return [
        "CORRELATIONS",
        `  /VARIABLES= ${vars().join(" ") || "?"}`,
        "  /PRINT=TWOTAIL NOSIG",
        `  /METHOD=${(values.method || "pearson").toUpperCase()} .`,
      ].join("\n");
    case "reliability":
      return [
        "RELIABILITY",
        `  /VARIABLES=${vars().join(" ") || "?"}`,
        "  /SCALE('ALL VARIABLES') ALL",
        "  /MODEL=ALPHA .",
      ].join("\n");
    case "cluster":
      return [
        `QUICK CLUSTER ${vars().join(" ") || "?"}`,
        `  /CRITERIA=CLUSTER(${values.k || "AUTO"}) MXITER(10)`,
        "  /METHOD=KMEANS .",
      ].join("\n");
    case "pca":
      return [
        "FACTOR",
        `  /VARIABLES ${vars().join(" ") || "?"}`,
        "  /EXTRACTION PC",
        `  /CRITERIA FACTORS(${values.nComponents || 2}) .`,
      ].join("\n");
    case "turf":
      return [
        `TURF ${vars().join(" ") || "?"}`,
        `  /COMBINATION SIZE(${values.nItems || 3})`,
        "  /CRITERIA=MAXIMIZE REACH .",
      ].join("\n");
    case "descriptive":
      return [`DESCRIPTIVES VARIABLES=${vars().join(" ") || "?"}`, "  /STATISTICS=MEAN STDDEV MIN MAX ." ].join("\n");
    default:
      return `* ${analysisType.label} — configuration:\n${JSON.stringify(values, null, 2)}`;
  }
}
